import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { toggleWishlist, isInLocalWishlist, getWishlistIds } from "../lib/wishlist";
import { useApp } from "../context/AppContext";

/**
 * Heart button to add/remove product from wishlist.
 * Reads user email from AppContext if available, else falls back to localStorage.
 */
export default function WishlistHeart({ productId, size = 22, className = "" }) {
  const { user } = useApp() || {};
  const email = user?.email || null;
  const [active, setActive] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (email) {
        const ids = await getWishlistIds(email);
        if (mounted) setActive(ids.includes(productId));
      } else {
        if (mounted) setActive(isInLocalWishlist(productId));
      }
    })();
    const onChange = () => {
      if (email) {
        getWishlistIds(email).then((ids) => mounted && setActive(ids.includes(productId)));
      } else {
        setActive(isInLocalWishlist(productId));
      }
    };
    window.addEventListener("wishlist-changed", onChange);
    return () => { mounted = false; window.removeEventListener("wishlist-changed", onChange); };
  }, [email, productId]);

  async function onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      const added = await toggleWishlist(productId, email);
      setActive(added);
      toast.success(added ? "Ajouté à vos favoris" : "Retiré des favoris");
    } catch {
      toast.error("Erreur — réessayez");
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={active ? "Retirer des favoris" : "Ajouter aux favoris"}
      aria-pressed={active}
      data-testid={`wishlist-heart-${productId}`}
      className={`group inline-flex items-center justify-center rounded-full p-2 hover:bg-white/70 transition ${className}`}
    >
      <Heart
        size={size}
        className={`transition ${active ? "fill-coral text-coral" : "text-ink/70 group-hover:text-coral"}`}
      />
    </button>
  );
}
